import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useGroupLabels from 'hooks/useGroupLabels';
import PropTypes from 'prop-types';
import styles from './ProgramDesigner.module.css';
import { Plus, Minus, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import Tooltip from 'components/common/Tooltip';
import {
  canCombineRow,
  combineAgesMessage,
  combineTickLabel,
} from 'utils/combineAges';

// Identity key for matching program rows to picker options. Rows bind by
// (sourceGroupID, gender) — never by display name — so renaming, re-ranging
// or deleting the club age group can't blank the dropdown
// (age-group-lifecycle-design.md §7.1). Picker base groups carry gender null,
// which matches a program row's 'Mixed'.
const groupOptionKey = (sourceGroupID, gender) =>
  `${sourceGroupID}|${gender || 'Mixed'}`;

const ProgramDesigner = ({
  programData = { rotationCount: 4, programGroups: [] },
  clubProgramGroups = { ageGroups: [], athleteGroups: [] },
  clubEventTypes = [],
  onChange,
  maxEvents = 20, // Made configurable with a default
}) => {
  // "Age Groups", or "Year Groups" for a club that groups by year level.
  const { words, groupingBasis } = useGroupLabels();
  const [rotationCount, setRotationCount] = useState(4);
  const [programGroups, setProgramGroups] = useState([]);
  const [recentLocations, setRecentLocations] = useState([]);
  const [isInternalUpdate, setIsInternalUpdate] = useState(false);
  // Index of the group row pending delete confirmation; null = popup closed
  const [groupIndexToRemove, setGroupIndexToRemove] = useState(null);
  // A Combine ages tick waiting for the admin to read its message (step 8):
  // { index, combining }; null = no message open. Nothing changes until they
  // confirm, and nothing is saved until the meet is saved.
  const [combineChange, setCombineChange] = useState(null);
  const [showRemoveEventConfirm, setShowRemoveEventConfirm] = useState(false);

  // Build program group options from club settings
  const programGroupOptions = useMemo(() => {
    let clubAgeGroupOptions = [];
    let athleteGroupOptions = [];

    if (clubProgramGroups?.ageGroups?.length) {
      clubAgeGroupOptions = clubProgramGroups.ageGroups
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((group) => ({
          ...group,
          value: groupOptionKey(group.sourceGroupID, group.gender),
          label: group.displayName,
          groupSource: 'AgeGroup',
        }));
    }

    if (clubProgramGroups?.athleteGroups?.length) {
      athleteGroupOptions = clubProgramGroups.athleteGroups
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((group) => ({
          ...group,
          value: groupOptionKey(group.sourceGroupID, group.gender),
          label: group.displayName,
          groupSource: 'AthleteGroup',
        }));
    }

    // Omit empty groups so the shelved Athlete Groups feature (unlisted in
    // the admin menu) leaves no stray optgroup heading for clubs without any.
    return [
      {
        label: words.groups,
        options: clubAgeGroupOptions,
      },
      {
        label: 'Athlete Groups',
        options: athleteGroupOptions,
      },
    ].filter((group) => group.options.length > 0);
  }, [clubProgramGroups, words.groups]);

  // Identity keys of every live picker option — a program row whose key isn't
  // here has lost its source (deleted group, or gender-split change) and keeps
  // its snapshot label via a synthetic option instead of going blank.
  const liveOptionValues = useMemo(() => {
    const values = new Set();
    programGroupOptions.forEach((optGroup) =>
      optGroup.options.forEach((opt) => values.add(opt.value))
    );
    return values;
  }, [programGroupOptions]);

  // Build event type options from club settings
  // Track category events are grouped by classification (Sprint, Distance, etc.)
  // All other categories (Field Events, Training, System, etc.) are grouped by category
  const eventTypeOptions = useMemo(() => {
    if (!clubEventTypes?.length) return [];

    // Group events by the appropriate key
    const grouped = clubEventTypes.reduce((acc, eventType) => {
      // For Track category, group by classification; otherwise group by category
      const key =
        eventType.category === 'Track'
          ? eventType.classification || 'Other'
          : eventType.category || 'Other';
      if (!acc[key]) acc[key] = [];
      acc[key].push(eventType);
      return acc;
    }, {});

    // Transform to dropdown format, filtering out empty groups
    return Object.entries(grouped)
      .filter(([, options]) => options.length > 0)
      .map(([label, options]) => ({
        label,
        options: options
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((et) => ({
            value: String(et.eventCode),
            label: et.displayName,
          })),
      }));
  }, [clubEventTypes]);

  // Add location to recent list
  const addToRecentLocations = useCallback((location) => {
    setRecentLocations((prev) => {
      const locationSet = new Set(prev);
      locationSet.add(location);
      return [...locationSet];
    });
  }, []);

  // Initialize from programData prop only when it's an external update
  useEffect(() => {
    // Only update if this is an external change (not triggered by our own onChange)
    if (!isInternalUpdate) {
      if (programData?.rotationCount) {
        setRotationCount(programData.rotationCount);
      }
      if (programData?.programGroups) {
        setProgramGroups(programData.programGroups);
        const locationSet = new Set();
        programData.programGroups.forEach((group) => {
          group.events.forEach((event) => {
            if (event.location) locationSet.add(event.location);
          });
        });
        setRecentLocations([...locationSet]);
      }
    }
  }, [programData, isInternalUpdate]);

  // Debounced onChange to prevent rapid firing
  const debouncedOnChange = useMemo(() => {
    let timeoutId;
    return (data) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (onChange) {
          setIsInternalUpdate(true);
          onChange(data, '');
          // Reset the flag after a brief delay
          setTimeout(() => setIsInternalUpdate(false), 100);
        }
      }, 300); // 300ms debounce
    };
  }, [onChange]);

  // Trigger onChange when data changes (but not on initial mount)
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const newData = { rotationCount, programGroups };
    debouncedOnChange(newData);
  }, [rotationCount, programGroups, debouncedOnChange]);

  // Add rotation (event column)
  const handleAddRotation = useCallback(() => {
    if (rotationCount < maxEvents) {
      setRotationCount((prev) => prev + 1);
      setProgramGroups((prev) =>
        prev.map((group) => ({
          ...group,
          events: [...(group.events || []), { eventType: '', location: '' }],
        }))
      );
    }
  }, [rotationCount, maxEvents]);

  // Remove rotation (event column) — runs after the user confirms in the popup
  const handleRemoveRotation = useCallback(() => {
    if (rotationCount > 2) {
      setRotationCount((prev) => prev - 1);
      setProgramGroups((prev) =>
        prev.map((group) => ({
          ...group,
          events: group.events.slice(0, -1),
        }))
      );
    }
  }, [rotationCount]);

  const handleConfirmRemoveRotation = useCallback(() => {
    handleRemoveRotation();
    setShowRemoveEventConfirm(false);
  }, [handleRemoveRotation]);

  // Add age group row
  const handleAddAgeGroup = useCallback(() => {
    const newGroup = {
      id: Date.now(), // Temporary ID for React key
      groupName: '',
      displayName: '',
      groupSource: '',
      sourceGroupID: '',
      ageRange: '',
      gender: 'Mixed',
      maxEventsForGroup: null, // §6.4 — null = no limit
      combineAges: false, // step 8 — ticked only after the admin reads its message
      events: Array(rotationCount)
        .fill(null)
        .map(() => ({ eventType: '', location: '' })),
    };
    setProgramGroups((prev) => [...prev, newGroup]);
  }, [rotationCount]);

  // Remove age group (runs after the user confirms in the popup)
  const handleRemoveAgeGroup = useCallback((index) => {
    setProgramGroups((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // The names of the club's events by code, for the Combine ages message.
  const eventNameByCode = useMemo(
    () =>
      new Map(
        (clubEventTypes || []).map((eventType) => [
          Number(eventType.eventCode),
          eventType.displayName,
        ])
      ),
    [clubEventTypes]
  );

  // The message for a Combine ages tick (utils/combineAges.js): the row's
  // group, ages and events, in the club's words.
  const combineMessage = useMemo(() => {
    if (!combineChange) return null;
    const row = programGroups[combineChange.index];
    if (!row) return null;
    const eventNames = [
      ...new Set(
        (row.events || [])
          .map((event) => Number(event.eventType))
          // Rest periods and empty cells (codes from 1000) are not events.
          .filter((code) => code > 0 && code < 1000)
          .map((code) => eventNameByCode.get(code))
          .filter(Boolean)
      ),
    ];
    return combineAgesMessage({
      basis: groupingBasis,
      groupName: row.groupName || row.displayName,
      ageRange: row.ageRange,
      gender: row.gender,
      eventNames,
      combining: combineChange.combining,
    });
  }, [combineChange, programGroups, eventNameByCode, groupingBasis]);

  const handleConfirmCombine = useCallback(() => {
    if (combineChange) {
      setProgramGroups((prev) =>
        prev.map((group, index) =>
          index === combineChange.index
            ? { ...group, combineAges: combineChange.combining }
            : group
        )
      );
    }
    setCombineChange(null);
  }, [combineChange]);

  const handleConfirmRemoveAgeGroup = useCallback(() => {
    if (groupIndexToRemove !== null) {
      handleRemoveAgeGroup(groupIndexToRemove);
    }
    setGroupIndexToRemove(null);
  }, [groupIndexToRemove, handleRemoveAgeGroup]);

  // Update program group selection (value is the identity key of the picked option)
  const handleProgramGroupChange = useCallback(
    (index, value) => {
      // Check for duplicate selection
      setProgramGroups((prev) => {
        const isDuplicate = prev.some(
          (group, i) =>
            i !== index &&
            group.sourceGroupID &&
            groupOptionKey(group.sourceGroupID, group.gender) === value
        );

        if (isDuplicate) {
          toast.error('This group has already been added to the program');
          return prev;
        }

        const updated = [...prev];
        let allGroupOptions = [];
        programGroupOptions.forEach((g) => allGroupOptions.push(...g.options));
        const selectedOption = allGroupOptions.find(
          (opt) => opt.value === value
        );

        updated[index] = {
          ...updated[index],
          groupName: selectedOption?.sourceGroupName || '',
          displayName: selectedOption?.displayName || '',
          groupSource: selectedOption?.groupSource || '',
          sourceGroupID: selectedOption?.sourceGroupID || '',
          ageRange: selectedOption?.ageRange || '',
          gender: selectedOption?.gender || 'Mixed',
          displayOrder: selectedOption?.sortOrder || '',
          // A freshly picked option is by definition a live group.
          sourceGroupExists: true,
          // Another group: its Combine ages starts unticked (step 8).
          combineAges: false,
        };
        return updated;
      });
    },
    [programGroupOptions]
  );

  // Update event field
  const handleEventChange = useCallback(
    (groupIndex, eventIndex, field, value) => {
      setProgramGroups((prev) => {
        const updated = [...prev];
        if (!updated[groupIndex].events[eventIndex]) {
          updated[groupIndex].events[eventIndex] = {
            eventType: '',
            location: '',
          };
        }

        const processedValue =
          field === 'eventType' && value !== '' ? parseInt(value, 10) : value;

        updated[groupIndex].events[eventIndex] = {
          ...updated[groupIndex].events[eventIndex],
          [field]: processedValue,
        };
        return updated;
      });
    },
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.title}>Event Program</h3>
        </div>
      </div>

      <div className={styles.rotationControls}>
        <span className={styles.eventCount}>
          {rotationCount === 1 ? '1 Event' : `${rotationCount} Events`}
        </span>
        <CustomNewButton
          onClick={handleAddRotation}
          disabled={rotationCount >= maxEvents}
          variant="outline"
          customStyle={{
            backgroundColor: '#4a90e2',
            color: 'white',
            border: '1px solid #4a90e2',
            padding: '8px 16px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Plus size={16} />
          Add Event
        </CustomNewButton>
        <CustomNewButton
          onClick={() => setShowRemoveEventConfirm(true)}
          disabled={rotationCount <= 2}
          variant="outline"
          customStyle={{
            backgroundColor: '#4a90e2',
            color: 'white',
            border: '1px solid #4a90e2',
            padding: '8px 16px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Minus size={16} />
          Remove Event
        </CustomNewButton>
      </div>

      <div className={styles.programGridWrapper}>
        <div className={styles.programGrid}>
          {/* Fixed column - the groups, headed in the club's word: Age Groups or Year Groups */}
          <div className={styles.fixedColumn}>
            <div className={styles.fixedHeaderCell}>{words.groups}</div>
            {programGroups.map((group, groupIndex) => {
              const rowKey = group.sourceGroupID
                ? groupOptionKey(group.sourceGroupID, group.gender)
                : '';
              // Loaded row whose source no longer matches a live picker option
              // (source deleted, or its gender split changed) — keep it labeled
              // with its snapshot name via a synthetic, non-selectable option.
              const isOrphanRow = rowKey !== '' && !liveOptionValues.has(rowKey);
              return (
                <div
                  key={group.id || groupIndex}
                  className={styles.fixedGroupCell}
                >
                  <div className={styles.groupCellStack}>
                    <div className={styles.ageGroupCell}>
                      <select
                        className={styles.ageSelect}
                        value={rowKey}
                        style={{ background: 'white' }}
                        onChange={(e) =>
                          handleProgramGroupChange(groupIndex, e.target.value)
                        }
                      >
                        <option value="">Select Group</option>
                        {isOrphanRow && (
                          <option value={rowKey} disabled>
                            {(group.displayName || group.groupName) +
                              (group.sourceGroupExists === false
                                ? ' (deleted)'
                                : '')}
                          </option>
                        )}
                        {programGroupOptions.map((optGroup) => (
                          <optgroup key={optGroup.label} label={optGroup.label}>
                            {optGroup.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {group.sourceGroupExists === false && (
                        <Tooltip
                          text={`The club ${words.lower.group} this row came from has been deleted. The row still works for this meet, but if you remove it you won't be able to add it back.`}
                          delayMs={300}
                        >
                          <AlertTriangle size={16} color="#b45309" />
                        </Tooltip>
                      )}
                    </div>
                    {canCombineRow(group) && (
                      <label className={styles.combineAges}>
                        <input
                          type="checkbox"
                          checked={Boolean(group.combineAges)}
                          onChange={() =>
                            setCombineChange({
                              index: groupIndex,
                              combining: !group.combineAges,
                            })
                          }
                        />
                        {combineTickLabel(groupingBasis)}
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
            <div className={styles.fixedAddButtonCell}>
              <CustomNewButton
                onClick={handleAddAgeGroup}
                variant="outline"
                customStyle={{
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  border: '1px solid #4a90e2',
                  padding: '8px 16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Plus size={16} />
                Add Group
              </CustomNewButton>
            </div>
          </div>

          {/* Scrollable events section */}
          <div className={styles.scrollableSection}>
            <div className={styles.eventsGrid}>
              {/* Events header */}
              <div className={styles.eventsHeader}>
                {Array.from({ length: rotationCount }, (_, i) => (
                  <div key={i} className={styles.eventHeaderCell}>
                    Event {i + 1}
                  </div>
                ))}
                <div className={styles.actionHeaderCell}></div>
              </div>

              {/* Events rows */}
              {programGroups.map((group, groupIndex) => (
                <div key={group.id || groupIndex} className={styles.eventsRow}>
                  {Array.from({ length: rotationCount }, (_, eventIndex) => {
                    const event = group.events?.[eventIndex] || {
                      eventType: '',
                      location: '',
                    };
                    return (
                      <div key={eventIndex} className={styles.eventCell}>
                        <select
                          className={styles.eventSelect}
                          value={event.eventType || ''}
                          onChange={(e) =>
                            handleEventChange(
                              groupIndex,
                              eventIndex,
                              'eventType',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Event</option>
                          {eventTypeOptions.map((group) => (
                            <optgroup key={group.label} label={group.label}>
                              {group.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <input
                          className={styles.locationInput}
                          list={`locations-${groupIndex}-${eventIndex}`}
                          placeholder="Location"
                          value={event.location || ''}
                          onChange={(e) =>
                            handleEventChange(
                              groupIndex,
                              eventIndex,
                              'location',
                              e.target.value
                            )
                          }
                          onBlur={(e) => {
                            if (e.target.value) {
                              addToRecentLocations(e.target.value);
                            }
                          }}
                        />
                        <datalist id={`locations-${groupIndex}-${eventIndex}`}>
                          {recentLocations.map((loc) => (
                            <option key={loc} value={loc} />
                          ))}
                        </datalist>
                      </div>
                    );
                  })}
                  <div className={styles.actionCell}>
                    <Tooltip
                      text={`Remove this ${words.lower.group} row from the program`}
                      delayMs={1000}
                    >
                      <CustomNewButton
                        onClick={() => setGroupIndexToRemove(groupIndex)}
                        customStyle={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          padding: '6px',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                        }}
                      >
                        ×
                      </CustomNewButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.helpText}>
        <p>
          <strong>💡 Tip:</strong> You can optionally use the location field to
          specify event locations and times (e.g., Back straight, Discus A, 3:00
          PM).
        </p>
      </div>

      <CustomNewConfirm
        isOpen={Boolean(combineMessage)}
        onClose={() => setCombineChange(null)}
        title={combineMessage?.title || ''}
        description={
          combineMessage && (
            <>
              {combineMessage.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </>
          )
        }
        onConfirm={handleConfirmCombine}
        confirmButtonText={combineMessage?.confirmLabel}
      />

      <CustomNewConfirm
        isOpen={groupIndexToRemove !== null}
        onClose={() => setGroupIndexToRemove(null)}
        title={`Remove ${
          (groupIndexToRemove !== null &&
            programGroups[groupIndexToRemove]?.displayName) ||
          `this ${words.lower.group}`
        } from the program?`}
        description="This deletes the whole row, including all of its events. To remove the last event column instead, use the Remove Event button above."
        onConfirm={handleConfirmRemoveAgeGroup}
        confirmButtonText="Remove Group"
        confirmButtonVariant="danger"
      />

      <CustomNewConfirm
        isOpen={showRemoveEventConfirm}
        onClose={() => setShowRemoveEventConfirm(false)}
        title={`Remove Event ${rotationCount}?`}
        description={`This removes the last event column (Event ${rotationCount}) from every row of the program, including any events and locations entered in it.`}
        onConfirm={handleConfirmRemoveRotation}
        confirmButtonText="Remove Event"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

ProgramDesigner.propTypes = {
  programData: PropTypes.shape({
    rotationCount: PropTypes.number,
    programGroups: PropTypes.arrayOf(
      PropTypes.shape({
        groupSource: PropTypes.string,
        groupName: PropTypes.string,
        ageRange: PropTypes.string,
        gender: PropTypes.oneOf(['Mixed', 'M', 'F']),
        displayOrder: PropTypes.number,
        sourceGroupID: PropTypes.string,
        sourceGroupExists: PropTypes.bool,
        maxEventsForGroup: PropTypes.number,
        combineAges: PropTypes.bool,
        events: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
  clubProgramGroups: PropTypes.shape({
    ageGroups: PropTypes.arrayOf(
      PropTypes.shape({
        sourceGroupID: PropTypes.string,
        sourceGroupName: PropTypes.string,
        displayName: PropTypes.string,
        groupType: PropTypes.string,
        gender: PropTypes.string,
        isGenderSubgroup: PropTypes.bool,
        ageRange: PropTypes.string,
        sortOrder: PropTypes.number,
        memberCount: PropTypes.number,
      })
    ),
    athleteGroups: PropTypes.arrayOf(
      PropTypes.shape({
        sourceGroupID: PropTypes.string,
        sourceGroupName: PropTypes.string,
        displayName: PropTypes.string,
        groupType: PropTypes.string,
        gender: PropTypes.string,
        isGenderSubgroup: PropTypes.bool,
        ageRange: PropTypes.string,
        sortOrder: PropTypes.number,
        memberCount: PropTypes.number,
      })
    ),
  }),
  clubEventTypes: PropTypes.arrayOf(
    PropTypes.shape({
      eventTypeID: PropTypes.string,
      eventCode: PropTypes.number,
      displayName: PropTypes.string,
      category: PropTypes.string,
      classification: PropTypes.string,
      sortOrder: PropTypes.number,
      isActive: PropTypes.bool,
    })
  ),
  onChange: PropTypes.func,
  onImportClick: PropTypes.func,
  maxEvents: PropTypes.number,
};

export default ProgramDesigner;
